import { Component } from '@theme/component';

/** The single open instance — opening one closes any other. @type {PlwSelect | null} */
let openInstance = null;

/**
 * `<plw-select>` — PDP-style dropdown panel for a native `<select>`.
 *
 * Progressive enhancement (snippets/plw-select.liquid): the caller's EXISTING
 * native `<select>` stays in the DOM as the value carrier — every listener that
 * already hangs off it (plw-cart-line, plw-cart-quantity, plw-locale-select,
 * the localization form) keeps working untouched. This element layers the
 * elevated presentation on top: a trigger button + a `popover="manual"` panel
 * of server-rendered `role="option"` buttons, glued to the trigger in the top
 * layer (it tracks scroll/resize and flips above the trigger when there's no
 * room below). Choosing an option writes `select.value`, dispatches a bubbling
 * `change` on the select, then re-syncs the trigger text from the select — so
 * a listener that synchronously reverts the value (plw-cart-line on an
 * unavailable combination) is reflected immediately.
 *
 * Before the element is defined (and with JS off) the native select is the
 * visible, operable control (see `plw-select:not(:defined)` in base.css);
 * once defined the select is demoted to a hidden data carrier.
 *
 * Positioning/dismiss mirrors assets/plw-variant-selector.js (the PDP
 * Color/Size selector), which stays self-contained because its flip logic is
 * group-aware and it routes to a mobile sheet. This one is anchored at every
 * width (cart drawer, footer, nav drawer).
 *
 * Drawer interplay: Escape is swallowed on BOTH keydown (cart-drawer's
 * DialogComponent closes on bubbling keydown) and the matching keyup
 * (header-drawer closes on keyup) so escaping the dropdown never closes the
 * host drawer. Clicks inside the panel count as inside the cart dialog
 * (`isClickedOutside` uses `contains`, and the popover is a DOM descendant).
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} trigger - The trigger button.
 * @property {HTMLElement} panel - The popover panel.
 * @property {HTMLElement} list - The role="listbox" list.
 * @property {HTMLElement} [value] - The current-value text inside the trigger.
 *
 * @extends Component<Refs>
 */
class PlwSelect extends Component {
  requiredRefs = ['trigger', 'panel', 'list'];

  /** @type {HTMLSelectElement | null} */
  #select = null;
  #rafId = 0;
  #swallowEscapeKeyup = false;

  connectedCallback() {
    super.connectedCallback();

    this.#select = this.querySelector('select');
    if (this.#select) {
      // Demote the native control: it only carries the value from here on.
      this.#select.tabIndex = -1;
      this.#select.setAttribute('aria-hidden', 'true');
      this.#select.addEventListener('change', this.#onNativeChange);
    }

    this.refs.panel.addEventListener('toggle', this.#onPanelToggle);
    this.addEventListener('keydown', this.#onKeydown);
    this.addEventListener('keyup', this.#onKeyup);
    this.#sync();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#select?.removeEventListener('change', this.#onNativeChange);
    this.refs.panel?.removeEventListener('toggle', this.#onPanelToggle);
    this.removeEventListener('keydown', this.#onKeydown);
    this.removeEventListener('keyup', this.#onKeyup);
    this.#close({ focusTrigger: false });
    this.#teardownTracking();
  }

  /** Opens or closes the panel (trigger `on:click`). */
  toggle() {
    const { panel } = this.refs;
    if (panel.matches(':popover-open')) {
      this.#close();
      return;
    }
    if (openInstance && openInstance !== this) openInstance.#close({ focusTrigger: false });
    this.#sync();
    // Show hidden first so #position() (run on the `toggle` event) can measure
    // the real size before paint — no below→above / overflow flash.
    panel.classList.add('is-positioning');
    try {
      panel.showPopover();
    } catch {
      panel.classList.remove('is-positioning');
    }
  }

  /**
   * Option activation — writes the native select and notifies its listeners.
   * @param {Event} event - The click/keyboard event (target = the option button).
   */
  choose(event) {
    const option = /** @type {HTMLElement | null} */ (
      event.target instanceof Element ? event.target.closest('[role="option"][data-value]') : null
    );
    if (!option || option.getAttribute('aria-disabled') === 'true') return;

    this.#close();

    const select = this.#select;
    if (!select) return;
    const value = option.dataset.value ?? '';
    if (select.value !== value) {
      select.value = value;
      // A real <select> fires a bubbling, non-cancelable `change`; mirror it so
      // plw-cart-line / plw-cart-quantity / plw-locale-select react as before.
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }
    // Re-read — a listener may have reverted the value synchronously.
    this.#sync();
  }

  /* ---- State sync ---- */

  #onNativeChange = () => this.#sync();

  /** @returns {HTMLElement[]} */
  #options() {
    return /** @type {HTMLElement[]} */ ([...this.refs.list.querySelectorAll('[role="option"]')]);
  }

  /** Reflects `select.value` onto the option list + the trigger's value text. */
  #sync() {
    const value = this.#select?.value;
    if (value == null) return;
    /** @type {HTMLElement | undefined} */
    let selected;
    for (const option of this.#options()) {
      const on = option.dataset.value === value;
      option.setAttribute('aria-selected', String(on));
      option.classList.toggle('is-selected', on);
      if (on) selected = option;
    }
    const { value: valueRef } = this.refs;
    if (!selected || !valueRef) return;
    const label =
      selected.dataset.triggerLabel ?? selected.querySelector('.plw-select__option-label')?.textContent?.trim() ?? '';
    if (label && valueRef.textContent !== label) valueRef.textContent = label;
  }

  /* ---- Open / close ---- */

  /** @param {{ focusTrigger?: boolean }} [options] */
  #close({ focusTrigger = true } = {}) {
    const { panel, trigger } = this.refs;
    if (!panel?.matches(':popover-open')) return;
    try {
      panel.hidePopover();
    } catch {
      /* already hidden */
    }
    if (focusTrigger) trigger?.focus({ preventScroll: true });
  }

  /** @param {Event} event */
  #onPanelToggle = (event) => {
    const open = /** @type {ToggleEvent} */ (event).newState === 'open';
    this.refs.trigger.setAttribute('aria-expanded', String(open));
    if (open) {
      openInstance = this;
      this.#setupTracking();
      const target = this.#options().find((o) => o.getAttribute('aria-selected') === 'true') ?? this.#options()[0];
      target?.focus({ preventScroll: true });
    } else {
      if (openInstance === this) openInstance = null;
      this.#teardownTracking();
    }
  };

  #setupTracking() {
    this.#position();
    this.refs.panel.classList.remove('is-positioning');
    window.addEventListener('scroll', this.#onScrollResize, { passive: true, capture: true });
    window.addEventListener('resize', this.#onScrollResize, { passive: true });
    document.addEventListener('pointerdown', this.#onOutsidePointer, true);
  }

  #teardownTracking() {
    this.refs.panel?.classList.remove('is-positioning');
    window.removeEventListener('scroll', this.#onScrollResize, { capture: true });
    window.removeEventListener('resize', this.#onScrollResize);
    document.removeEventListener('pointerdown', this.#onOutsidePointer, true);
    if (this.#rafId) cancelAnimationFrame(this.#rafId);
    this.#rafId = 0;
  }

  #onScrollResize = () => {
    if (this.#rafId) return;
    this.#rafId = requestAnimationFrame(() => {
      this.#rafId = 0;
      this.#position();
    });
  };

  /** Close on a pointer down outside the panel and the trigger (which toggles itself). */
  #onOutsidePointer = (event) => {
    const { panel, trigger } = this.refs;
    if (!panel.matches(':popover-open')) return;
    const target = /** @type {Node} */ (event.target);
    if (panel.contains(target) || trigger.contains(target)) return;
    this.#close({ focusTrigger: false });
  };

  /**
   * Glue the panel to the trigger: at least as wide as the trigger, natural
   * width otherwise, clamped into the viewport; flips above when there's no
   * room below (the footer switcher sits at the page bottom).
   */
  #position() {
    const { trigger, panel } = this.refs;
    const rect = trigger.getBoundingClientRect();
    const gap = 4;
    const edge = 12;

    panel.style.minWidth = `${Math.round(rect.width)}px`;
    const width = panel.offsetWidth;
    const height = panel.offsetHeight;
    const spaceBelow = window.innerHeight - rect.bottom;
    const flipUp = spaceBelow < height + gap && rect.top > spaceBelow;
    const maxLeft = Math.max(edge, window.innerWidth - edge - width);
    const left = Math.min(Math.max(rect.left, edge), maxLeft);

    panel.style.left = `${Math.round(left)}px`;
    panel.style.top = flipUp ? `${Math.round(rect.top - height - gap)}px` : `${Math.round(rect.bottom + gap)}px`;
  }

  /* ---- Keyboard ---- */

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    const { panel, trigger } = this.refs;
    const open = panel.matches(':popover-open');
    const target = event.target instanceof Element ? event.target : null;

    if (event.key === 'Escape') {
      if (!open) return;
      // Keep the host drawer open: cart-drawer closes on bubbling keydown,
      // header-drawer on keyup — swallow both.
      event.preventDefault();
      event.stopPropagation();
      this.#swallowEscapeKeyup = true;
      this.#close();
      return;
    }

    if (!open) {
      if (target && trigger.contains(target) && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
        event.preventDefault();
        this.toggle();
      }
      return;
    }

    if (event.key === 'Tab') {
      // Let focus move on naturally; the panel just closes.
      this.#close({ focusTrigger: false });
      return;
    }

    const options = this.#options();
    if (!options.length) return;
    const current = target ? options.findIndex((o) => o === target || o.contains(target)) : -1;

    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowUp': {
        event.preventDefault();
        const delta = event.key === 'ArrowDown' ? 1 : -1;
        const next = current === -1 ? 0 : (current + delta + options.length) % options.length;
        options[next]?.focus({ preventScroll: true });
        return;
      }
      case 'Home':
      case 'End':
        event.preventDefault();
        options[event.key === 'Home' ? 0 : options.length - 1]?.focus({ preventScroll: true });
        return;
      case 'Enter':
      case ' ':
        if (current !== -1) {
          event.preventDefault();
          this.choose(event);
        }
        return;
      default:
        break;
    }

    // First-letter type-ahead (cycles from the focused option).
    if (event.key.length === 1 && !event.altKey && !event.ctrlKey && !event.metaKey) {
      const char = event.key.toLocaleLowerCase();
      for (let step = 1; step <= options.length; step++) {
        const candidate = options[(current + step) % options.length];
        const text = candidate?.textContent?.trim().toLocaleLowerCase() ?? '';
        if (text.startsWith(char)) {
          event.preventDefault();
          candidate?.focus({ preventScroll: true });
          return;
        }
      }
    }
  };

  /** @param {KeyboardEvent} event */
  #onKeyup = (event) => {
    if (event.key !== 'Escape' || !this.#swallowEscapeKeyup) return;
    this.#swallowEscapeKeyup = false;
    event.stopPropagation();
  };
}

if (!customElements.get('plw-select')) {
  customElements.define('plw-select', PlwSelect);
}
